<template>
  <component :is="asForm ? BaseForm : 'div'" ref="attributeSelectionFormRef" :form="attributeSelection">
    <section
      v-for="({ groupedOptions, label, type }, i) in variantAttributes"
      :key="type"
      :class="{ '<lg:op-0': showOnlyFirstSelectorOnMobile && i !== 0, 'mt-6': i !== 0 }"
      :data-scroll-el="`${type}Pickers`"
      :data-test-id="`${type}-groups`"
    >
      <div :class="{ 'flex items-center space-x-2': showBadge }">
        <div v-if="showBadge" class="h-4 w-4 flex justify-center rounded-full bg-grey-10">
          <span class="text-xs c-white fw-bold">{{ i + 1 }}</span>
        </div>
      </div>
      <vf-form-error v-if="type !== 'color' && error && !attributeSelection[type]" class="mt-1">
        {{ isDigitalProduct ? $t.selectCardAmountError : $t.selectSizeError }}
      </vf-form-error>
      <div
        v-for="({ options }, j) in groupedOptions"
        :key="j"
        :data-test-id="`${type}-groups-item`"
      >
        <fieldset v-if="type === 'color'" :aria-labelledby="colorId" class="space-y-2">
          <vf-carousel
            class-container="gap-1 p-1 -m-1 <lg:w-screen <lg:scroll-px-4 <lg:px-4 <lg:-mx-4 lg:wrap"
            class-controls="!hidden"
            @pointerdown.stop
          >
            <vf-color-picker
              v-for="({ available, id, label: optionLabel, price, swatch, url, value }, k) in
                options.sort((a, b) => a.id === firstSelectedProduct ? -1 : b.id === firstSelectedProduct ? 1 : 0)"
              :key="optionLabel"
              ref="colorOptionsRef"
              :active="!pending && getIsActive(value, id)"
              :class="{ 'lg:hidden': colorOptionsShown < (k + 1) }"
              :color="swatch?.color"
              :loading="getIsPending(value, id, pending)"
              :name="optionLabel"
              :sale="$feature.showOnSaleSwatchIndicatorOnPDP && price.lowCurrent < price.lowOriginal"
              :size="colorPickerSize"
              :thumbnail="swatch?.image"
              :unavailable="!isComingSoon && (!available && !pending)"
              :variant="colorPickerShape"
              @click="selectNewColor(enableColorAsAttribute ? value : id, url, optionLabel)"
            />
            <base-link
              v-if="$feature.showCustomizeOnPDP && product.customsExternalUrl"
              v-style="customizeStyles"
              class="flex flex-col items-center justify-center rounded @hactive:bg-grey-70 bg-brand-2 body-4 <lg:text-micro"
              :to="product.customsExternalUrl"
              @click="$gtm.push('pdpPage.onCustomize')"
            >
              <vf-icon name="customs" size="md" />
              <span>{{ $t.customize }}</span>
            </base-link>
            <base-button
              v-if="colorOptionsShown < options.length"
              ref="showMoreColorsRef"
              v-style="container"
              class="flex flex-col items-center justify-center rounded @hactive:bg-grey-70 bg-brand-2 <lg:hidden"
              @click="showMoreColors"
            >
              <span class="subtitle-4">+{{ options.length - colorOptionsMax }}</span>
              <span class="body-4">{{ $t.more }}</span>
            </base-button>
            <base-button
              v-else-if="colorOptionsExpanded"
              v-style="container"
              class="flex flex-col items-center justify-center rounded @hactive:bg-grey-70 bg-brand-2 <lg:hidden"
              @click="showLessColors"
            >
              <span class="ws-pre text-center body-4">{{ $t.showLess }}</span>
            </base-button>
          </vf-carousel>
          <p class="text-xs">
            <span :id="colorId">{{ label }}</span>: {{ attributeSelectionLabels[type] }}
          </p>
        </fieldset>
        <fieldset v-else>
          <legend class="subtitle-4" :class="{ 'sr-only': !$feature.pdpShowDropdownLabel }">
            {{ label }}
          </legend>
          <base-form-field
            v-slot="{ attrs: { onChange } }"
            :name="type"
            :rule="validateRequired()"
            :selector="`${type}Pickers`"
          >
            <vf-size-select
              v-model="attributeSelection[type]"
              class="mt-2"
              show-placeholder-stock-hint
              :unavailable="!pending && !options.find(({ value }) => value === attributeSelection[type])?.available"
              @change="onChange"
              @click="handleSizeSelect(variantAttributes[i])"
            >
              {{ attributeSelectionLabels[type] || $t.selectAttribute[type] }}
            </vf-size-select>
          </base-form-field>
        </fieldset>
      </div>
    </section>
    <panel-variant-selection :size="{ sm: 'sm', lg: 'full' }" />
  </component>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'
import type { Responsive } from '#types/common'
import type { ColorPickerStyles } from '#types/config/components/vf/colorPicker'
import type {
  AttributeSelection,
  ColorSwatchShape,
  ProductAttributeType,
  ProductExtended,
  SelectedVariant
} from '#types/product'
import type { SizesSubUnion } from '#types/sizes'
import { BaseForm } from '#components'

const { colorSize, colorSwatch, handleNextAttribute, isComingSoon, product } = defineProps<{
  /*
  * variants, showNotifyMe, productInventory props are added only for consistency with other brands
  * They aren't used in the component
  * but allows to avoid situations with incorrect props resolution when rendering the DOM
  */
  attributeSelectionLabels: Record<string, string>
  colorSize?: SizesSubUnion<'sm' | 'md' | 'lg'> | Responsive<SizesSubUnion<'sm' | 'md' | 'lg'>>
  colorSwatch?: ColorSwatchShape
  error?: boolean
  handleNextAttribute?: () => void
  isComingSoon?: boolean
  isDigitalProduct?: boolean
  pending?: boolean
  product: ProductExtended
  selectedVariant?: SelectedVariant | null
  showBadge?: boolean
  showOnlyFirstSelectorOnMobile?: boolean
  asForm?: boolean
  showNotifyMe?: boolean
  variants?: any
}>()

const emit = defineEmits<{
  selectColor: [id: string, url: string, optionLabel: string]
  selectSize: [type: Exclude<ProductAttributeType, 'color'>, value: string, label: string]
}>()

const config = useAppConfig()
const { brandStyles } = config.components.vf.colorPicker
const { enableColorAsAttribute, swatches } = config.pages.pdp
const colorId = useId()
const { $feature, $t } = useNuxtApp()
const colorOptionsRef = useTemplateRef('colorOptionsRef')
const showMoreColorsRef = useTemplateRef('showMoreColorsRef')

const attributeSelection = defineModel<AttributeSelection>({ required: true })
const attributeSelectionFormRef = defineModel<InstanceType<typeof BaseFormType> | null>('formRef', { required: false })

const PanelVariantSelection = createPanel('variant-selection')

const colorOptionsExpanded = ref(false)
const selectedProductId = ref()

const colorPickerShape = colorSwatch ?? swatches.variant
const colorPickerSize = colorSize ?? swatches.size

const { container } = Object.entries(brandStyles[colorPickerShape]).reduce((acc: ColorPickerStyles, [key, value]) => {
  acc[key] = {}

  for (const styleProp in value) {
    acc[key][styleProp] = replaceValues(isObject(colorPickerSize)
      ? colorPickerSize
      : { sm: colorPickerSize }, value[styleProp])
  }

  return acc
}, {})

const customizeColSpan = $t.customize.length < 10 ? 1 : 2

const customizeStyles = {
  ...container,
  w: {
    sm: container?.w?.sm
      && `${(Number(container.w.sm.replace('rem', '')) * customizeColSpan + 0.25 * (customizeColSpan - 1))}rem`,
    md: container?.w?.md
      && `${(Number(container.w.md.replace('rem', '')) * customizeColSpan + 0.25 * (customizeColSpan - 1))}rem`,
    lg: container?.w?.lg
      && `${(Number(container.w.lg.replace('rem', '')) * customizeColSpan + 0.25 * (customizeColSpan - 1))}rem`
  }
}

// When the user hits the PDP for the first time, we store the first selected product to show it as first on the color picker
const firstSelectedProduct = product.id

const colorOptionsMax = computed(
  () => 9 - (($feature.showCustomizeOnPDP && product.customsExternalUrl) ? customizeColSpan : 0)
)

const colorOptionsShown = computed(
  () => colorOptionsExpanded.value ? Number.POSITIVE_INFINITY : colorOptionsMax.value
)

const variantAttributes = computed(() => isComingSoon
  ? product.attributes.filter((attribute) => attribute.type === 'color')
  : product.attributes
)

const getIsActive = (value: string, id: string) => {
  return enableColorAsAttribute ? attributeSelection.value.color === value : product.id === id
}

const getIsPending = (value: string, id: string, pending?: boolean) => {
  if (!pending) return
  return enableColorAsAttribute
    ? attributeSelection.value.color === value
    : selectedProductId.value === id
}

const selectNewColor = (id, url, optionLabel) => {
  selectedProductId.value = id
  emit('selectColor', id, url, optionLabel)
}

const showLessColors = () => {
  colorOptionsExpanded.value = false
  nextTick(() => showMoreColorsRef.value?.[0]?.$el.focus())
}

const showMoreColors = () => {
  colorOptionsExpanded.value = true
  nextTick(() => colorOptionsRef.value?.[colorOptionsMax.value]?.$el.focus())
}

const handleSizeSelect = async (attribute) => {
  PanelVariantSelection.open({
    attribute,
    enableReserveInStore: $feature.allowProductReservationOnPDP && product?.isRobisOnly,
  }).then((payload) => {
    if (payload?.handleNextAttribute) handleNextAttribute?.()
  })
}

defineExpose({
  validate: () => attributeSelectionFormRef.value?.validate()
})
</script>
